import { useLayoutEffect, useRef, useState } from 'react';

/**
 * 텍스트가 컨테이너보다 길 때 marquee 애니메이션을 적용하는 커스텀 훅
 * @param dependency - 텍스트가 변경될 때마다 애니메이션 여부를 재계산할 의존성
 * @returns { shouldAnimate, textRef, containerRef } - 애니메이션 여부와 필요한 ref들
 */
export const useMarqueeAnimation = (dependency?: string) => {
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependency 인자로 강제 재측정
  useLayoutEffect(() => {
    const container = containerRef.current;
    const textEl = textRef.current;

    if (!container || !textEl) {
      setShouldAnimate(false);
      return;
    }

    const update = () => {
      const c = containerRef.current;
      const t = textRef.current;
      if (!c || !t) return;
      const overflow = t.scrollWidth > c.clientWidth + 1;
      setShouldAnimate((prev) => (prev === overflow ? prev : overflow));
    };

    update();
    queueMicrotask(() => {
      requestAnimationFrame(update);
    });

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const ro = new ResizeObserver(update);
    ro.observe(container);
    return () => ro.disconnect();
  }, [dependency]);

  return {
    shouldAnimate,
    textRef,
    containerRef
  };
};
